import { Box, Button, Divider, HStack, ScrollView, Text, VStack, useColorModeValue, Center, Heading } from 'native-base';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as AppActions from 'src/redux/actions/appAction';
import * as LoginActions from 'src/redux/actions/loginAction';
import * as SettingsActions from 'src/redux/actions/settingsAction';
import { IStore } from 'src/redux/reducers';
import { containerStyle } from 'src/styles/containerStyle';
import { getText } from 'src/utils/i18n';
import { isElectron } from 'src/utils/miscUtils';
import { UpdatesSettings } from 'src/components/UpdatesSettings';

interface StorageInfo {
  jobCount: number;
  totalSizeBytes: number;
  maxJobCount: number;
  maxStorageBytes: number;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export default function DesktopSettingsPage() {
  const dispatch = useDispatch();

  const preferredProcessingMode = useSelector((store: IStore) => store.settings.preferredProcessingMode);
  const isOnline = useSelector((store: IStore) => store.app.isOnline);
  const userLoggedIn = useSelector((store: IStore) => store.login.userLoggedIn);
  const cloudAuth = useSelector((store: IStore) => store.app.cloudAuth);

  const [storageInfo, setStorageInfo] = useState<StorageInfo | null>(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);

  const refreshStorageInfo = useCallback(async () => {
    if (typeof window !== 'undefined' && (window as any).electron?.getStorageInfo) {
      try {
        const info = await (window as any).electron.getStorageInfo();
        setStorageInfo(info);
      } catch (err) {
        console.warn('Failed to retrieve storage info:', err);
      }
    }
  }, []);

  useEffect(() => {
    refreshStorageInfo();
  }, [refreshStorageInfo]);

  const handleCleanup = useCallback(async () => {
    if (typeof window !== 'undefined' && (window as any).electron?.applyStoragePolicy) {
      setCleanupRunning(true);
      try {
        await (window as any).electron.applyStoragePolicy({ deleteSynced: true });
        await refreshStorageInfo();
      } catch (err) {
        console.warn('Storage cleanup failed:', err);
      } finally {
        setCleanupRunning(false);
      }
    }
  }, [refreshStorageInfo]);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');
  const containerBg = useColorModeValue('gray.50', 'gray.900');

  if (!isElectron()) {
    return (
      <Box bg={containerBg} style={containerStyle.mainContainer}>
        <Center flex={1}>
          <VStack space={3} alignItems="center" p={8} bg={cardBg} borderRadius="xl" shadow="2" maxW="400px">
            <Heading size="md">{getText('desktopSettings')}</Heading>
            <Text color={secondaryTextColor} textAlign="center">
              {getText('desktopOnlyMessage')}
            </Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  const cloudAuthStatus = cloudAuth?.status || 'unknown';
  const cloudAuthLabel =
    cloudAuthStatus === 'valid'
      ? getText('cloudAuthSignedIn')
      : cloudAuthStatus === 'invalid'
        ? getText('cloudAuthExpired')
        : getText('cloudAuthUnknown');
  const cloudAuthDotColor =
    cloudAuthStatus === 'valid'
      ? '#22c55e'
      : cloudAuthStatus === 'invalid'
        ? '#f97316'
        : '#94a3b8';
  const canSyncNow = isOnline && userLoggedIn === 'LOGGED_IN' && cloudAuthStatus !== 'invalid';

  return (
    <Box bg={containerBg} style={containerStyle.mainContainer}>
      <ScrollView>
        <Center>
          <VStack p={8} space={8} maxW="800px" w="100%">
            <VStack space={2}>
              <Heading size="lg">{getText('processingMode')}</Heading>
              <Text fontSize="sm" color={secondaryTextColor}>
                {getText('processingModeDescription')}
              </Text>
            </VStack>

            <Box 
              bg={cardBg} 
              borderWidth={1} 
              borderColor={borderColor} 
              borderRadius="2xl" 
              p={6}
              shadow="3"
            >
              <VStack space={4}>
                <HStack space={3} alignItems="center">
                  <Text fontSize="md" fontWeight="600">
                    {getText('onlineStatus')}
                  </Text>
                  <Box
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: isOnline ? '#22c55e' : '#ef4444',
                    }}
                  />
                  <Text fontSize="md" fontWeight="500" color={isOnline ? 'success.600' : 'error.600'}>
                    {isOnline ? getText('online') : getText('offline')}
                  </Text>
                </HStack>

                <HStack space={3} alignItems="center" flexWrap="wrap">
                  <Text fontSize="md" fontWeight="600">
                    {getText('cloudSignInLabel')}
                  </Text>
                  <Box
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: cloudAuthDotColor,
                    }}
                  />
                  <Text fontSize="md" fontWeight="500" color={secondaryTextColor}>
                    {cloudAuthLabel}
                  </Text>
                  {cloudAuthStatus === 'invalid' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      isDisabled={!isOnline}
                      onPress={() => {
                        dispatch(AppActions.resetCloudAuthStatus());
                        dispatch(LoginActions.setUserLoggedIn('NOT_LOGGED_IN'));
                      }}
                    >
                      {getText('reAuthenticate')}
                    </Button>
                  ) : null}
                </HStack>

                <HStack space={3} alignItems="center" flexWrap="wrap">
                  <Button
                    variant={preferredProcessingMode === 'local' ? 'solid' : 'outline'}
                    size="md"
                    px={6}
                    onPress={() => dispatch(SettingsActions.setPreferredProcessingMode('local'))}
                  >
                    {getText('processingModeLocal')}
                  </Button>
                  <Button
                    variant={preferredProcessingMode === 'cloud' ? 'solid' : 'outline'}
                    size="md"
                    px={6}
                    onPress={() => dispatch(SettingsActions.setPreferredProcessingMode('cloud'))}
                  >
                    {getText('processingModeCloud')}
                  </Button>
                </HStack>

                <>
                  <Divider my={2} />
                  <VStack space={3}>
                    <Button
                      size="md"
                      isDisabled={!canSyncNow}
                      onPress={() => dispatch(AppActions.syncLocalJobsNow())}
                    >
                      {getText('syncNow')}
                    </Button>
                    <Text fontSize="sm" color={secondaryTextColor}>
                      {getText('syncDescription')}
                    </Text>
                  </VStack>
                </>
              </VStack>
            </Box>

            <VStack space={2}>
              <Heading size="lg">App Updates</Heading>
              <Text fontSize="sm" color={secondaryTextColor}>
                {getText('appUpdatesDescription')}
              </Text>
            </VStack>

            <Box 
              bg={cardBg} 
              borderWidth={1} 
              borderColor={borderColor} 
              borderRadius="2xl" 
              overflow="hidden"
              shadow="3"
            >
              <UpdatesSettings />
            </Box>

            <VStack space={2}>
              <Heading size="lg">{getText('localStorageHeading')}</Heading>
              <Text fontSize="sm" color={secondaryTextColor}>
                Local job records and files are automatically removed when the count exceeds{' '}
                {storageInfo ? storageInfo.maxJobCount.toLocaleString() : '1,000'} jobs or total
                size exceeds{' '}
                {storageInfo ? formatBytes(storageInfo.maxStorageBytes) : '10 GB'}.
                Oldest jobs are deleted first.
              </Text>
            </VStack>

            <Box
              bg={cardBg}
              borderWidth={1}
              borderColor={borderColor}
              borderRadius="2xl"
              p={6}
              shadow="3"
            >
              <VStack space={4}>
                {storageInfo ? (
                  <>
                    <HStack space={6} flexWrap="wrap">
                      <VStack space={1}>
                        <Text fontSize="xs" color={secondaryTextColor}>
                          {getText('jobsStoredLabel')}
                        </Text>
                        <Text fontSize="md" fontWeight="600">
                          {storageInfo.jobCount.toLocaleString()} / {storageInfo.maxJobCount.toLocaleString()}
                        </Text>
                      </VStack>
                      <VStack space={1}>
                        <Text fontSize="xs" color={secondaryTextColor}>
                          {getText('diskUsageLabel')}
                        </Text>
                        <Text fontSize="md" fontWeight="600">
                          {formatBytes(storageInfo.totalSizeBytes)} / {formatBytes(storageInfo.maxStorageBytes)}
                        </Text>
                      </VStack>
                    </HStack>
                    <Divider />
                  </>
                ) : null}
                <HStack space={3} alignItems="center" flexWrap="wrap">
                  <Button
                    size="md"
                    variant="outline"
                    isLoading={cleanupRunning}
                    onPress={handleCleanup}
                  >
                    {getText('cleanUpNow')}
                  </Button>
                  <Text fontSize="sm" color={secondaryTextColor} flex={1}>
                    {getText('cleanUpDescription')}
                  </Text>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </Center>
      </ScrollView>
    </Box>
  );
}
